import {describeMmoSync} from '../services/mmoSyncJob';
import { useState, useEffect, useRef } from 'react';
import {
  Settings2, Sparkles, RefreshCw, ExternalLink, PlayCircle, StopCircle,
  Film, History, Clock, AlertCircle, CheckCircle2, Copy, Check, ShieldCheck, Wand2,
  Activity, Video, FileSpreadsheet, Flame, Bot, Send
} from 'lucide-react';
import UgcBatchesQueueView from './UgcBatchesQueueView';
import {
  formatGoogleFlowPrompts, getUgcFlowkitBatch, setExplicitlySelectedBatchId,
  type UgcFlowkitLaunchConfig, type UgcFlowkitSelectionPreview, type UgcFlowkitBatch
} from '../services/ugcFlowkit';
import { useStore } from '../services/store';
import {
  syncMmoNiches, getLatestMmoSync, monitorMmoSync, stopMmoSync, type MmoSyncResult,
  fetchWinProductsInventory,
  fetchResearchMappingTable,
  runAffiliateScriptStudio,
  runChannelHealthCoach,
  deconstructWinningVideo,
  fetchHarnessLedger,
  fetchHermesStatus,
  triggerHermesPatrol,
  askHermesAssistantClient,
  evaluateHermesClusterModelsClient,
  saveHermesMessagingConfigClient,
  sendHermesMessagingReportClient,
  sendInteractiveMessagingChatClient,
  pollHermesMessagingClient,
  type MmoNicheId,
  type MmoNicheDefinition,
  type ScriptTone,
  type WinProductInventoryItem,
  type CompetitorVideoMappingEntry,
  type AffiliateScriptResponse,
  type ChannelHealthReport,
  type ViralBlueprintResult,
  type HarnessLedgerSnapshot,
  type HermesAssistantState,
  type HermesChatReply,
  type ClusterModelEvaluationReport,
  type HermesMessagingStatus,
} from '../services/affiliateHarnessClient';

const ALL_MMO_NICHES: readonly MmoNicheDefinition[] = [
  { id: 'snacks', label: 'Đồ ăn vặt', group: 'food' },
  { id: 'candy', label: 'Bánh kẹo', group: 'food' },
  { id: 'sauce', label: 'Mắm', group: 'food' },
  { id: 'dried', label: 'Đồ khô', group: 'food' },
  { id: 'farm', label: 'Nông sản', group: 'food' },
  { id: 'kitchen', label: 'Nhà bếp', group: 'extended' },
  { id: 'health', label: 'Sức khỏe', group: 'extended' },
  { id: 'beauty', label: 'Mỹ phẩm', group: 'extended' },
  { id: 'skincare', label: 'Chăm sóc da', group: 'extended' },
  { id: 'women', label: 'Thời trang nữ', group: 'extended' },
  { id: 'men', label: 'Thời trang nam', group: 'extended' },
  { id: 'home', label: 'Gia dụng', group: 'extended' },
  { id: 'baby', label: 'Mẹ và bé', group: 'extended' },
  { id: 'phone', label: 'Phụ kiện điện thoại', group: 'extended' },
  { id: 'cases', label: 'Ốp lưng điện thoại', group: 'extended' },
  { id: 'pets', label: 'Thú cưng', group: 'extended' },
  { id: 'hair', label: 'Chăm sóc tóc', group: 'extended' },
  { id: 'shoes', label: 'Giày dép', group: 'extended' },
  { id: 'bags', label: 'Túi ví', group: 'extended' },
  { id: 'sports', label: 'Thể thao', group: 'extended' },
] as const;

const VOICE_OPTIONS: readonly { id: ScriptTone; label: string; desc: string }[] = [
  { id: 'natural', label: 'Tự nhiên & thân thiện', desc: 'Gần gũi như chia sẻ thật cho bạn bè' },
  { id: 'fun', label: 'Vui nhộn & hài hước', desc: 'Năng lượng cao, bắt trend GenZ vui miệng' },
  { id: 'story', label: 'Kể chuyện ngôi thứ nhất', desc: 'Dẫn dắt trải nghiệm cá nhân lôi cuốn' },
  { id: 'expert', label: 'Chuyên gia đáng tin cậy', desc: 'Phân tích sành ăn, chứng minh chất lượng' },
] as const;

const SAMPLE_CHANNEL_JSON = JSON.stringify({
  channel: { handle: 'dacsanvietnam_24', followers: 1852, videos: 103 },
  summary: {
    views: 583252,
    medianViews: 263,
    engagementRate: 0.0037,
    postsPerWeek: 25.1,
    shopShare: 0.786,
    adShare: 0.766,
  },
  videos: [
    [2, 119900, 145, 7, 13, 39, 25, 1, 1, 0],
    [12, 87400, 394, 30, 81, 144, 21, 1, 1, 0],
    [13, 84500, 293, 9, 44, 83, 21, 1, 1, 0],
    [16, 15, 2, 0, 0, 1, 67, 1, 0, 0],
    [19, 10, 1, 0, 0, 0, 73, 0, 0, 0],
  ],
  top: [
    { views: 119900, engagementRate: 0.0016, shop: true, ad: true, caption: 'Mắm tép đồng nguyên chất miền Tây' },
    { views: 87400, engagementRate: 0.0058, shop: true, ad: true, caption: 'Chân gà rút xương ăn liền tiện lợi' },
  ],
  bottom: [
    { views: 10, engagementRate: 0.1, shop: false, ad: false, caption: 'Sứa biển ăn liền tiện lợi giòn sần sật' },
  ],
}, null, 2);

export interface MasterUgcV3PanelProps {
  ugcFlowkitConfig: UgcFlowkitLaunchConfig;
  updateUgcFlowkitConfig: <K extends keyof UgcFlowkitLaunchConfig>(key: K, value: UgcFlowkitLaunchConfig[K]) => void;
  ugcFlowkitStarting: boolean;
  handleStartUgcFlowkit: (pipelineVersion?: 'v2' | 'v3') => Promise<void>;
  handlePreviewUgcFlowkit: () => Promise<void>;
  currentUgcFlowkitPreview: UgcFlowkitSelectionPreview | null;
  selectedUgcFlowkitProducts: { id: string; name: string }[];
  selectedUgcFlowkitProductIds: Set<string>;
  toggleUgcFlowkitProduct: (id: string, checked: boolean) => void;
  setUgcFlowkitSelectedProductIds: (ids: string[]) => void;
  v2Batch: UgcFlowkitBatch | null;
  setV2Batch: (batch: UgcFlowkitBatch | null) => void;
  v2BatchLoading: boolean;
  refreshV2Batch: () => Promise<void>;
  v2ActionLoading: string | null;
  handleResumeV2Batch: () => Promise<void>;
  handleStopV2Batch: () => Promise<void>;
  handleResumeProduct: (productId: string) => Promise<void>;
  copiedPromptId: string | null;
  handlePromptCopy: (text: string, id: string) => void;
  ugcFlowkitBatchId: string | null;
  setUgcFlowkitBatchId: (id: string | null) => void;
  v2RightTab?: 'prompts' | 'batches';
  setV2RightTab?: React.Dispatch<React.SetStateAction<'prompts' | 'batches'>>;
  onOpenWorkspace: () => void;
}

export default function MasterUgcV3Panel(props: MasterUgcV3PanelProps) {
  const {
    ugcFlowkitConfig, updateUgcFlowkitConfig, ugcFlowkitStarting,
    handleStartUgcFlowkit, handlePreviewUgcFlowkit,
    currentUgcFlowkitPreview, selectedUgcFlowkitProducts, selectedUgcFlowkitProductIds,
    toggleUgcFlowkitProduct, setUgcFlowkitSelectedProductIds,
    v2Batch, setV2Batch, v2BatchLoading, refreshV2Batch,
    v2ActionLoading, handleResumeV2Batch, handleStopV2Batch, handleResumeProduct,
    copiedPromptId, handlePromptCopy, ugcFlowkitBatchId, setUgcFlowkitBatchId,
    v2RightTab, setV2RightTab, onOpenWorkspace,
  } = props;

  const [v3RightTab, setV3RightTab] = useState<'prompts' | 'batches' | 'scriptStudio' | 'channelViral' | 'ledger'>('prompts');

  useEffect(() => {
    if (v2RightTab === 'batches' || v2RightTab === 'prompts') {
      setV3RightTab(v2RightTab);
    }
  }, [v2RightTab]);
  const [selectedNiches, setSelectedNiches] = useState<MmoNicheId[]>(['snacks', 'candy', 'sauce', 'dried', 'farm']);
  const [showExtendedNiches, setShowExtendedNiches] = useState(false);
  const [selectedTone, setSelectedTone] = useState<ScriptTone>('natural');

  // MMO Sync & Automated Today_Win_Products <-> Win_Products state
  const [mmoSyncing, setMmoSyncing] = useState(false);
  const [mmoJobId,setMmoJobId] = useState<string>();
  const [mmoStopping,setMmoStopping] = useState(false);
  const mmoMonitor = useRef<AbortController|null>(null);
  const [mmoSyncSummary, setMmoSyncSummary] = useState<string>('');
  const [syncedWinInventory, setSyncedWinInventory] = useState<WinProductInventoryItem[]>([]);
  const [syncedMappingTable, setSyncedMappingTable] = useState<CompetitorVideoMappingEntry[]>([]);
  const [winSyncActions, setWinSyncActions] = useState<Record<string, 'copied_from_win_products' | 'downloaded_new_win_images'>>({});
  const [showWinSyncList, setShowWinSyncList] = useState(false);

  // Script Studio state
  const [scriptProductName, setScriptProductName] = useState('(V1) Muối Tiêu Sọ Lá Chanh Sặc Siêu Cay (Chay) - Đặc Sản Tây Ninh');
  const [scriptPriceText, setScriptPriceText] = useState('28.899 ₫');
  const [scriptSold, setScriptSold] = useState(178422);
  const [scriptQuestions, setScriptQuestions] = useState('Chay ăn được không?, Chấm trái cây hay hải sản hợp hơn?');
  const [scriptObjections, setScriptObjections] = useState('Sợ mặn gắt, Sợ lá chanh bị đắng');
  const [scriptLoading, setScriptLoading] = useState(false);
  const [scriptResult, setScriptResult] = useState<AffiliateScriptResponse | null>(null);
  const [scriptError, setScriptError] = useState('');

  // Channel Health & Youwee Deconstructor state
  const [channelJsonText, setChannelJsonText] = useState(SAMPLE_CHANNEL_JSON);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthReport, setHealthReport] = useState<ChannelHealthReport | null>(null);
  const [healthError, setHealthError] = useState('');

  const [refVideoUrl, setRefVideoUrl] = useState('');
  const [deconLoading, setDeconLoading] = useState(false);
  const [deconResult, setDeconResult] = useState<ViralBlueprintResult | null>(null);

  // Ledger state
  const [ledgerSnapshot, setLedgerSnapshot] = useState<HarnessLedgerSnapshot | null>(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const toggleNiche = (id: MmoNicheId) => {
    setSelectedNiches(prev => {
      const next = prev.includes(id) ? prev.filter(n => n !== id) : [...prev, id];
      updateUgcFlowkitConfig('selectedNiches', next);
      return next;
    });
  };

  useEffect(() => {
    const controller=new AbortController();mmoMonitor.current=controller;
    Promise.all([fetchWinProductsInventory().catch(()=>({items:[]})),fetchResearchMappingTable().catch(()=>[]),getLatestMmoSync(controller.signal)])
      .then(async ([invRes,mapRes,job])=>{
        if(controller.signal.aborted)return;
        setSyncedWinInventory(invRes.items||[]);setSyncedMappingTable(Array.isArray(mapRes)?mapRes:[]);
        if(job?.status==='running'){
          setMmoSyncing(true);setMmoJobId(job.id);
          try {
            const result=await monitorMmoSync(job,j=>setMmoSyncSummary(describeMmoSync(j)),controller.signal);
            if(!controller.signal.aborted)applyMmoSyncResult(result);
          }catch(error){if(!controller.signal.aborted)setMmoSyncSummary(error instanceof Error?error.message:'Không theo dõi được đồng bộ.');}
          finally{if(!controller.signal.aborted)setMmoSyncing(false);}
        }else if(job?.status==='completed' && job.result){applyMmoSyncResult(job.result);}
        else if(job?.status==='failed' || job?.status==='cancelled'){setMmoSyncSummary(job.error||'Đồng bộ trước chưa hoàn tất.');}
      }).catch(()=>{});
    return ()=>{controller.abort();mmoMonitor.current?.abort();};
  }, []);

  const applyMmoSyncResult = (res:MmoSyncResult) => {
      const winList = res.winInventory ?? [];
      const mapList = res.mappingTable ?? [];
      setSyncedWinInventory(winList);
      setSyncedMappingTable(mapList);
      const actionsMap: Record<string, 'copied_from_win_products' | 'downloaded_new_win_images'> = {};
      for (const p of res.provisioned || []) {
        if (p.syncAction) {
          const baseFolder = p.folderPath.split('/').pop() || p.name;
          actionsMap[baseFolder] = p.syncAction;
          actionsMap[p.name] = p.syncAction;
        }
      }
      setWinSyncActions(actionsMap);
      setShowWinSyncList(true);
      const readyCount = winList.filter(w => w.curationReport?.policyVersion === 5 && w.curationReport.readyForProduction === true).length;
      const copiedCount = res.copiedFromWinCount ?? Object.values(actionsMap).filter(v => v === 'copied_from_win_products').length;
      const downloadedCount = res.downloadedNewWinCount ?? Object.values(actionsMap).filter(v => v === 'downloaded_new_win_images').length;
      setMmoSyncSummary(`Đồng bộ xong ${res.products.length} sản phẩm · ${copiedCount} sản phẩm đã có, ${downloadedCount} sản phẩm cập nhật ảnh · ${readyCount}/${winList.length} sản phẩm Win có bộ ảnh đạt kiểm định. Các bộ chưa đạt cần bổ sung ảnh trước khi tạo video.`);
  };
  const handleSyncMmo = async () => {
    mmoMonitor.current?.abort();
    const controller=new AbortController();mmoMonitor.current=controller;
    setMmoJobId(undefined);setMmoSyncing(true);setMmoSyncSummary('Đang chọn tối đa 10 Win tiềm năng hôm nay…');
    try {
      const res=await syncMmoNiches(selectedNiches,true,true,job=>{setMmoJobId(job.id);setMmoSyncSummary(describeMmoSync(job));},controller.signal);
      if(controller.signal.aborted)return;
      applyMmoSyncResult(res);await handlePreviewUgcFlowkit().catch(()=>{});
    } catch(err) {
      if(!controller.signal.aborted)setMmoSyncSummary(err instanceof Error?err.message:'Đồng bộ sản phẩm thất bại.');
    } finally {if(!controller.signal.aborted)setMmoSyncing(false);}
  };

  const handleStopMmo = async () => {
    if(!mmoJobId || mmoStopping)return;
    setMmoStopping(true);
    try {
      const stopped=await stopMmoSync(mmoJobId);
      mmoMonitor.current?.abort();setMmoSyncing(false);
      setMmoSyncSummary(stopped.error||'Đã dừng đồng bộ.');
      const inventory=await fetchWinProductsInventory();setSyncedWinInventory(inventory.items);
    }catch(error){setMmoSyncSummary(error instanceof Error?error.message:'Chưa dừng được tác vụ, hãy thử lại.');}
    finally{setMmoStopping(false);}
  };

  const handleGenerateScript = async () => {
    setScriptLoading(true);
    setScriptError('');
    try {
      const res = await runAffiliateScriptStudio({
        productName: scriptProductName,
        tone: selectedTone,
        priceText: scriptPriceText,
        sold: scriptSold,
        topQuestions: scriptQuestions.split(',').map(s => s.trim()).filter(Boolean),
        topObjections: scriptObjections.split(',').map(s => s.trim()).filter(Boolean),
        durationSec: ugcFlowkitConfig.durationSec,
      });
      setScriptResult(res);
    } catch (err) {
      setScriptError(err instanceof Error ? err.message : 'Không thể tạo kịch bản.');
    } finally {
      setScriptLoading(false);
    }
  };

  const handleRunChannelHealth = async () => {
    setHealthLoading(true);
    setHealthError('');
    try {
      const parsed = JSON.parse(channelJsonText);
      const report = await runChannelHealthCoach(parsed);
      setHealthReport(report);
    } catch (err) {
      setHealthError(err instanceof Error ? err.message : 'Dữ liệu JSON kênh không hợp lệ.');
    } finally {
      setHealthLoading(false);
    }
  };

  const handleApplyHarnessActions = (report: ChannelHealthReport) => {
    updateUgcFlowkitConfig('durationSec', report.harnessActions.recommendedDurationSec);
    setSelectedTone(report.harnessActions.recommendedTone);
    updateUgcFlowkitConfig('scriptTone', report.harnessActions.recommendedTone);
    setSelectedNiches(report.harnessActions.priorityNiches);
    updateUgcFlowkitConfig('selectedNiches', report.harnessActions.priorityNiches);
  };

  const handleDeconstructVideo = async () => {
    if (!refVideoUrl.trim()) return;
    setDeconLoading(true);
    try {
      const res = await deconstructWinningVideo({
        videoUrl: refVideoUrl.trim(),
        durationSec: ugcFlowkitConfig.durationSec,
      });
      setDeconResult(res);
    } finally {
      setDeconLoading(false);
    }
  };

  const loadLedger = async () => {
    setLedgerLoading(true);
    try {
      const snap = await fetchHarnessLedger();
      setLedgerSnapshot(snap);
    } finally {
      setLedgerLoading(false);
    }
  };

  // Hermes 24/7 Virtual Assistant state
  const [hermesState, setHermesState] = useState<HermesAssistantState | null>(null);
  const [hermesPatrolling, setHermesPatrolling] = useState(false);
  const [hermesQuestion, setHermesQuestion] = useState('Đề xuất sản phẩm Win hôm nay và hướng làm kịch bản 18 giây dễ nổ đơn nhất');
  const [hermesAsking, setHermesAsking] = useState(false);
  const [hermesReply, setHermesReply] = useState<HermesChatReply | null>(null);

  // Cluster Router Evaluation & Zalo/Telegram Messaging state
  const [showClusterMatrix, setShowClusterMatrix] = useState(true);
  const [clusterBenchmarking, setClusterBenchmarking] = useState(false);
  const [clusterReport, setClusterReport] = useState<ClusterModelEvaluationReport | null>(null);

  const [showMessagingPanel, setShowMessagingPanel] = useState(true);
  const [messagingStatus, setMessagingStatus] = useState<HermesMessagingStatus | null>(null);
  const [preferredPlatform, setPreferredPlatform] = useState<'zalo' | 'telegram' | 'both'>('zalo');
  const [zaloTokenInput, setZaloTokenInput] = useState('');
  const [zaloChatIdInput, setZaloChatIdInput] = useState('');
  const [telegramTokenInput, setTelegramTokenInput] = useState('');
  const [telegramChatIdInput, setTelegramChatIdInput] = useState('');
  const [messagingSaving, setMessagingSaving] = useState(false);
  const [messagingSending, setMessagingSending] = useState(false);
  const [messagingFeedback, setMessagingFeedback] = useState('');
  const [zaloDirectMessage, setZaloDirectMessage] = useState('Báo cáo nhanh sản phẩm Win hôm nay và gợi ý kịch bản dễ nổ đơn');
  const [zaloDirectSending, setZaloDirectSending] = useState(false);

  const loadHermesStatus = async () => {
    try {
      const st = await fetchHermesStatus();
      setHermesState(st);
      if (st.clusterRouting) setClusterReport(st.clusterRouting);
      if (st.messaging) {
        setMessagingStatus(st.messaging);
        setPreferredPlatform(st.messaging.preferredPlatform);
        if (st.messaging.zalo.chatId) setZaloChatIdInput(st.messaging.zalo.chatId);
        if (st.messaging.telegram.chatId) setTelegramChatIdInput(st.messaging.telegram.chatId);
      }
    } catch {
      // ignore initial status fetch error
    }
  };

  const handleHermesPatrolNow = async () => {
    setHermesPatrolling(true);
    try {
      const st = await triggerHermesPatrol();
      setHermesState(st);
      if (st.clusterRouting) setClusterReport(st.clusterRouting);
      if (st.messaging) setMessagingStatus(st.messaging);
    } finally {
      setHermesPatrolling(false);
    }
  };

  const handleRunClusterBenchmark = async () => {
    setClusterBenchmarking(true);
    try {
      const rep = await evaluateHermesClusterModelsClient(true);
      setClusterReport(rep);
    } finally {
      setClusterBenchmarking(false);
    }
  };

  const handleSaveMessagingConfig = async (connectAction: 'zalo' | 'telegram' | 'save' = 'save') => {
    setShowMessagingPanel(true);
    setMessagingSaving(true);
    setMessagingFeedback('⏳ Đang kết nối và xác thực cổng Zalo / Telegram với Hermes 24/7…');
    try {
      const payload: Parameters<typeof saveHermesMessagingConfigClient>[0] = {
        preferredPlatform: connectAction === 'zalo' ? 'zalo' : connectAction === 'telegram' ? 'telegram' : preferredPlatform,
        zaloChatId: zaloChatIdInput.trim() || 'zalo-admin-ddlab',
        telegramChatId: telegramChatIdInput.trim(),
        connectAction,
      };
      if (zaloTokenInput.trim()) payload.zaloBotToken = zaloTokenInput.trim();
      if (telegramTokenInput.trim()) payload.telegramBotToken = telegramTokenInput.trim();
      const st = await saveHermesMessagingConfigClient(payload);
      setMessagingStatus(st);
      if (st.zalo.chatId) setZaloChatIdInput(st.zalo.chatId);
      if (st.telegram.chatId) setTelegramChatIdInput(st.telegram.chatId);
      setZaloTokenInput('');
      setTelegramTokenInput('');
      setMessagingFeedback(
        `✅ ĐÃ KẾT NỐI THÀNH CÔNG (${st.preferredPlatform.toUpperCase()}): ${
          st.zalo.connected ? `🟢 ${st.zalo.botName || 'Hermes Zalo Bridge 24/7'} (Chat ID: ${st.zalo.chatId || 'zalo-admin-ddlab'}) đã sẵn sàng nhận lệnh & bắn báo cáo!` : ''
        } ${st.telegram.connected ? `🟢 ${st.telegram.botName || 'Telegram Bot'} đã kết nối!` : ''}`,
      );
      setTimeout(() => {
        document.getElementById('hermes-zalo-messaging-panel')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 80);
    } catch (err) {
      setMessagingFeedback(err instanceof Error ? `❌ ${err.message}` : '❌ Lưu cấu hình Zalo/Telegram thất bại.');
    } finally {
      setMessagingSaving(false);
    }
  };

  const handleSendTestMessagingReport = async () => {
    setMessagingSending(true);
    setMessagingFeedback('⏳ Đang tổng hợp báo cáo Hermes 24/7 và bắn qua Zalo / Telegram…');
    try {
      const res = await sendHermesMessagingReportClient({ platform: preferredPlatform });
      setMessagingStatus(res.status);
      if (res.sentZalo || res.sentTelegram) {
        setMessagingFeedback(
          `✅ Đã bắn báo cáo Hermes 24/7 thành công qua ${[
            res.sentZalo ? 'Zalo' : '',
            res.sentTelegram ? 'Telegram' : '',
          ]
            .filter(Boolean)
            .join(' & ')}! Xem nội dung chi tiết tại Nhật ký hội thoại bên dưới.`,
        );
      } else {
        setMessagingFeedback(
          `⚠️ Chưa gửi được tin nhắn: ${res.zaloError || res.telegramError || 'Hãy bấm Kết Nối Zalo Ngay hoặc nhập Bot Token.'}`,
        );
      }
    } catch (err) {
      setMessagingFeedback(err instanceof Error ? `❌ ${err.message}` : '❌ Không gửi được báo cáo Zalo/Telegram.');
    } finally {
      setMessagingSending(false);
    }
  };

  const handleSendInteractiveZaloChat = async () => {
    if (!zaloDirectMessage.trim()) return;
    setZaloDirectSending(true);
    setMessagingFeedback('⏳ Đang gửi tin nhắn Zalo tới Hermes 24/7 trên Cluster…');
    try {
      const res = await sendInteractiveMessagingChatClient({
        platform: preferredPlatform === 'telegram' ? 'telegram' : 'zalo',
        message: zaloDirectMessage.trim(),
        senderName: 'Bạn (Zalo Admin)',
      });
      setMessagingStatus(res.status);
      setMessagingFeedback(`✅ Hermes (${res.modelUsed}) đã phản hồi tin nhắn Zalo của bạn ở Nhật ký hội thoại bên dưới!`);
    } catch (err) {
      setMessagingFeedback(err instanceof Error ? `❌ ${err.message}` : '❌ Lỗi gửi tin nhắn Zalo.');
    } finally {
      setZaloDirectSending(false);
    }
  };

  const handlePollMessagingNow = async () => {
    setMessagingSending(true);
    try {
      const res = await pollHermesMessagingClient();
      setMessagingStatus(res.status);
      if (res.status.zalo.chatId) setZaloChatIdInput(res.status.zalo.chatId);
      if (res.status.telegram.chatId) setTelegramChatIdInput(res.status.telegram.chatId);
      setMessagingFeedback(`✅ Đã đồng bộ hộp thư Zalo/Telegram: xử lý ${res.processedCount} tin nhắn mới.`);
    } finally {
      setMessagingSending(false);
    }
  };

  const handleAskHermes = async () => {
    if (!hermesQuestion.trim()) return;
    setHermesAsking(true);
    try {
      const reply = await askHermesAssistantClient({
        message: hermesQuestion.trim(),
        productName: scriptProductName,
        niche: selectedNiches[0] || 'snacks',
      });
      setHermesReply(reply);
    } finally {
      setHermesAsking(false);
    }
  };

  useEffect(() => {
    void loadHermesStatus();
  }, []);

  useEffect(() => {
    if (v3RightTab === 'ledger') {
      void loadLedger();
    }
  }, [v3RightTab]);

  const activeClusterReport = clusterReport || hermesState?.clusterRouting || null;
  const activeMessaging = messagingStatus || hermesState?.messaging || null;

  return (
    <div className="ugc-pipeline-v3 flex-1 overflow-y-auto bg-[#fdf6e9] p-4 lg:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header intro */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#d6bd7c]/50 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="rounded bg-[#9e2a2b] px-2 py-0.5 text-xs font-black text-[#fff8ec]">v3 (Affiliate Harness)</span>
              <h1 className="text-lg font-black text-[#2f2414]">TikTok Affiliate Harness + Trợ Lý Ảo Hermes 24/7</h1>
              <span className="rounded-full border border-[#865916]/40 bg-[#865916]/10 px-2.5 py-0.5 text-[11px] font-bold text-[#865916]">
                Kế thừa 100% Pipeline v2 + Ông Chú MMO + Youwee + 5-Axis Viral Gate + Hermes 24/7
              </span>
            </div>
            <p className="mt-1 text-xs text-[#6d5b3f]">
              Mỗi ngày chọn tối đa 10 sản phẩm Win theo ngách, viết kịch bản AI 4 giọng văn, giải phẫu video triệu view qua Youwee, tuần tra 24/7 bằng Hermes Agent trên Agentic Cluster + kết nối Zalo / Telegram 2 chiều.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void refreshV2Batch()}
              disabled={v2BatchLoading}
              className="flex items-center gap-1.5 rounded-lg border border-[#d6bd7c] bg-[#fff8ec] px-3 py-1.5 text-xs font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${v2BatchLoading ? 'animate-spin' : ''}`} />
              Refresh Batch
            </button>
            <button
              type="button"
              onClick={onOpenWorkspace}
              className="flex items-center gap-1.5 rounded-lg bg-[#865916] px-3 py-1.5 text-xs font-bold text-[#fff8ec] hover:bg-[#6f4810] transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open Pipeline Workspace
            </button>
          </div>
        </div>

        {/* ── Hermes 24/7 Virtual Assistant Command Center ── */}
        <div className="rounded-2xl border border-[#9e2a2b]/45 bg-[#fff8ec] p-4 shadow-sm space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#9e2a2b] text-[#fff8ec]">
                <Bot className="w-4 h-4" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-black text-[#2f2414]">
                    Trợ Lý Ảo Hermes 24/7 (Agentic Cluster Hybrid + Zalo/Telegram)
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#9e2a2b]/15 px-2.5 py-0.5 text-[11px] font-bold text-[#9e2a2b]">
                    <span className="h-2 w-2 rounded-full bg-[#9e2a2b] animate-pulse" />
                    {hermesState?.gatewayActive !== false ? 'Đang chạy 24/7 (Cluster + Turbo v3)' : 'Chế độ chờ'}
                  </span>
                  <span className="text-[11px] font-semibold text-[#6d5b3f]">
                    {hermesState?.model
                      ? `${hermesState.model} · ${hermesState.provider}`
                      : 'cluster-auto (Local Qwen3.5-9B + Antigravity Turbo v3) · Agentic Cluster (8301 + 8305)'}
                  </span>
                </div>
                <p className="text-xs text-[#6d5b3f] mt-0.5">
                  {hermesState?.summaryVi || 'Đang trực chiến 24/7 trên Cluster của bạn: tự động đánh giá chọn model tối ưu theo từng task (Local GPU Qwen3.5-9B vs Antigravity Cloud Warm Pool ~2.5s) và kết nối 2 chiều qua Zalo / Telegram.'}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setShowClusterMatrix(v => !v)}
                className="rounded-lg border border-[#9e2a2b]/40 bg-[#fdf6e9] px-2.5 py-1.5 text-xs font-bold text-[#9e2a2b] hover:bg-[#9e2a2b]/10"
              >
                {showClusterMatrix ? 'Ẩn Bảng Đánh Giá Model' : '🧠 Bảng Tự Đánh Giá Model Cluster'}
              </button>
              <button
                type="button"
                disabled={messagingSaving}
                onClick={() => void handleSaveMessagingConfig('zalo')}
                className="rounded-lg bg-[#865916] px-3 py-1.5 text-xs font-bold text-[#fff8ec] hover:bg-[#6f4810] disabled:opacity-50"
              >
                {messagingSaving ? 'Đang kết nối Zalo…' : activeMessaging?.zalo.connected ? '🟢 Đã Kết Nối Zalo (Kiểm Tra Lại)' : '📲 Kết Nối Zalo Ngay'}
              </button>
              <button
                type="button"
                onClick={() => setShowMessagingPanel(v => !v)}
                className="rounded-lg border border-[#865916]/40 bg-[#fdf6e9] px-2.5 py-1.5 text-xs font-bold text-[#865916] hover:bg-[#865916]/10"
              >
                {showMessagingPanel ? 'Ẩn Khung Zalo/Telegram' : 'Mở Khung Zalo/Telegram'}
              </button>
              <button
                type="button"
                disabled={hermesPatrolling}
                onClick={() => void handleHermesPatrolNow()}
                className="flex items-center gap-1.5 rounded-lg bg-[#9e2a2b] px-3 py-1.5 text-xs font-bold text-[#fff8ec] hover:bg-[#7d1f20] disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${hermesPatrolling ? 'animate-spin' : ''}`} />
                {hermesPatrolling ? 'Đang tuần tra 24/7…' : 'Tuần Tra Ngay (Patrol 24/7)'}
              </button>
            </div>
          </div>

          {hermesState?.cronJobs && hermesState.cronJobs.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className="text-[11px] font-bold text-[#6d5b3f]">Lịch tự động 24/7:</span>
              {hermesState.cronJobs.map(job => (
                <span
                  key={job.id}
                  className="rounded-lg border border-[#d6bd7c] bg-[#fdf6e9] px-2 py-0.5 text-[11px] font-semibold text-[#2f2414]"
                >
                  ✓ {job.name} ({job.schedule})
                </span>
              ))}
              {hermesState.productTiers && (
                <span className="rounded-lg border border-[#9e2a2b]/40 bg-[#9e2a2b]/10 px-2 py-0.5 text-[11px] font-bold text-[#9e2a2b]">
                  Kho sẵn sàng: {hermesState.productTiers.Win_Products.length} Win · {hermesState.productTiers.Best_Products.length} Best · {hermesState.productTiers.Good_Products.length} Good
                </span>
              )}
            </div>
          )}

          {/* ── Panel A: Self-Evaluating Cluster + Antigravity Task-to-Model Matrix ── */}
          {showClusterMatrix && activeClusterReport && (
            <div className="rounded-xl border border-[#9e2a2b]/35 bg-[#fdf6e9] p-3.5 space-y-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-black text-[#2f2414]">
                      🧠 Cơ Chế Tự Đánh Giá Task-to-Model (Local Cluster GPU + Antigravity Cloud Turbo v3)
                    </span>
                    <span className="rounded bg-[#9e2a2b]/15 px-2 py-0.5 text-[10px] font-bold text-[#9e2a2b]">
                      Cluster Router (8301): {activeClusterReport.clusterRouterOnline ? 'ONLINE' : 'STANDBY'}
                    </span>
                    <span className="rounded bg-[#9e2a2b]/15 px-2 py-0.5 text-[10px] font-bold text-[#9e2a2b]">
                      {activeClusterReport.antigravityServiceVersion} (8305): {activeClusterReport.antigravityTurboOnline ? 'WARM POOL ~2.5s' : 'STANDBY'}
                    </span>
                    <span className="rounded bg-[#865916]/15 px-2 py-0.5 text-[10px] font-bold text-[#865916]">
                      Local GPU Ollama (11434): {activeClusterReport.localOllamaOnline ? 'ONLINE' : 'STANDBY'}
                    </span>
                  </div>
                  <p className="text-[11px] text-[#6d5b3f] mt-0.5">
                    Đã gỡ bỏ <code className="font-mono">nvidia/nemotron-3-super-120b-a12b</code>. Hermes hiện tự động chấm điểm năng lực × độ trễ thực tế (EMA) để chọn model phù hợp nhất cho từng tác vụ:
                  </p>
                </div>
                <button
                  type="button"
                  disabled={clusterBenchmarking}
                  onClick={() => void handleRunClusterBenchmark()}
                  className="rounded-lg bg-[#865916] px-3 py-1.5 text-[11px] font-bold text-[#fff8ec] hover:bg-[#6f4810] disabled:opacity-50"
                >
                  {clusterBenchmarking ? 'Đang đo độ trễ thực tế…' : '⚡ Benchmark Thực Tế (Local vs Antigravity)'}
                </button>
              </div>

              {activeClusterReport.liveBenchmark && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 rounded-lg border border-[#9e2a2b]/30 bg-white p-2.5 text-[11px]">
                  <div>
                    <span className="font-black text-[#865916]">🖥 Local GPU ({activeClusterReport.liveBenchmark.localModelTested}):</span>{' '}
                    <span className="font-bold text-[#9e2a2b]">{activeClusterReport.liveBenchmark.localLatencyMs} ms</span>
                    <div className="text-[#6d5b3f] truncate">→ "{activeClusterReport.liveBenchmark.localSampleReply}"</div>
                  </div>
                  <div>
                    <span className="font-black text-[#9e2a2b]">☁️ Antigravity Turbo v3 ({activeClusterReport.liveBenchmark.antigravityModelTested}):</span>{' '}
                    <span className="font-bold text-[#9e2a2b]">
                      {activeClusterReport.liveBenchmark.antigravityLatencyMs} ms {activeClusterReport.liveBenchmark.antigravityWarmHit ? '(Warm KV Cache Hit)' : ''}
                    </span>
                    <div className="text-[#6d5b3f] truncate">→ "{activeClusterReport.liveBenchmark.antigravitySampleReply}"</div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                {activeClusterReport.tasks.map(t => (
                  <div key={t.task_type} className="rounded-lg border border-[#d6bd7c] bg-white p-2.5 space-y-1">
                    <div className="text-[11px] font-black text-[#2f2414] leading-snug">{t.task_label}</div>
                    <div className="text-[11px] font-bold text-[#9e2a2b] break-all">
                      ★ {t.winner?.model || 'gemini-3.8-flash-low'}
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-[#6d5b3f]">
                      <span>{t.winner?.provider === 'local-cluster' ? '🖥 Local GPU' : '☁️ Antigravity Warm'}</span>
                      <span>Điểm: {t.winner?.total_score ?? 0.75} · ~{t.winner?.estimated_latency_ms ?? 2600}ms</span>
                    </div>
                    {t.fallback && (
                      <div className="text-[10px] text-[#865916] truncate">
                        Dự phòng: {t.fallback.model}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Panel B: 2-Way Zalo & Telegram Connector for Hermes 24/7 ── */}
          {showMessagingPanel && (
            <div id="hermes-zalo-messaging-panel" className="rounded-xl border border-[#865916]/40 bg-[#fdf6e9] p-3.5 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-black text-[#2f2414]">
                      📲 Kết Nối Trò Chuyện & Báo Cáo 24/7 Qua Zalo (Ưu Tiên) / Telegram (Dự Phòng)
                    </span>
                    <span className="rounded bg-[#9e2a2b]/15 px-2 py-0.5 text-[10px] font-bold text-[#9e2a2b]">
                      Plugin zalo-platform v2.1.3: ĐÃ CÀI ĐẶT
                    </span>
                    <span
                      className={`rounded px-2 py-0.5 text-[10px] font-bold ${
                        activeMessaging?.zalo.connected
                          ? 'bg-[#9e2a2b] text-[#fff8ec]'
                          : 'bg-[#865916]/15 text-[#865916]'
                      }`}
                    >
                      {activeMessaging?.zalo.connected
                        ? `🟢 ĐÃ KẾT NỐI ZALO: ${activeMessaging.zalo.botName || 'Hermes Zalo Bridge 24/7'}`
                        : '⚪ Zalo: Bấm "Kết Nối Zalo Ngay" bên dưới để kích hoạt'}
                    </span>
                    {activeMessaging?.telegram.connected && (
                      <span className="rounded bg-[#865916] px-2 py-0.5 text-[10px] font-bold text-[#fff8ec]">
                        🟢 Telegram: {activeMessaging.telegram.botName || 'Đã kết nối'}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-[#6d5b3f] mt-0.5">
                    Bấm <strong>"🔗 Kết Nối Zalo Ngay"</strong> để kích hoạt ngay Cổng Zalo 2 chiều trên hệ thống (hoặc dán <strong>ZALO_BOT_TOKEN</strong> từ <a href="https://bot.zaloplatforms.com" target="_blank" rel="noreferrer" className="underline text-[#9e2a2b] font-bold">bot.zaloplatforms.com</a> nếu muốn bắn thông báo về App Zalo trên điện thoại).
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <select
                    value={preferredPlatform}
                    onChange={e => setPreferredPlatform(e.target.value as 'zalo' | 'telegram' | 'both')}
                    className="rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1 text-xs font-bold text-[#2f2414]"
                  >
                    <option value="zalo">Ưu tiên Zalo Bot / Zalo Bridge</option>
                    <option value="telegram">Dùng Telegram Bot</option>
                    <option value="both">Chạy song song Zalo + Telegram</option>
                  </select>
                </div>
              </div>

              {messagingFeedback && (
                <div className="rounded-lg border border-[#9e2a2b] bg-[#9e2a2b]/15 px-3 py-2 text-xs font-bold text-[#2f2414]">
                  {messagingFeedback}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Zalo Config */}
                <div className="rounded-lg border border-[#d6bd7c] bg-white p-2.5 space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-1 text-xs font-bold text-[#2f2414]">
                    <span>1. Cổng Kết Nối Zalo 2 Chiều (Zalo Bridge & bot.zaloplatforms.com)</span>
                    <span className="text-[10px] text-[#9e2a2b]">
                      {activeMessaging?.zalo.connected
                        ? `🟢 Đang hoạt động (${activeMessaging.zalo.maskedToken})`
                        : 'Chưa kích hoạt'}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="password"
                      value={zaloTokenInput}
                      onChange={e => setZaloTokenInput(e.target.value)}
                      placeholder="ZALO_BOT_TOKEN (để trống nếu dùng Cổng Zalo Trực Tuyến)…"
                      className="rounded-lg border border-[#d6bd7c] px-2.5 py-1.5 text-xs text-[#2f2414]"
                    />
                    <input
                      type="text"
                      value={zaloChatIdInput}
                      onChange={e => setZaloChatIdInput(e.target.value)}
                      placeholder="Zalo Chat ID / SĐT (VD: zalo-admin-ddlab)"
                      className="rounded-lg border border-[#d6bd7c] px-2.5 py-1.5 text-xs text-[#2f2414]"
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 pt-0.5">
                    <button
                      type="button"
                      disabled={messagingSaving}
                      onClick={() => void handleSaveMessagingConfig('zalo')}
                      className="rounded-lg bg-[#9e2a2b] px-3 py-1.5 text-xs font-bold text-[#fff8ec] hover:bg-[#7d1f20] disabled:opacity-50"
                    >
                      {messagingSaving ? 'Đang kết nối Zalo…' : '🔗 Kết Nối Zalo Ngay'}
                    </button>
                    <a
                      href="https://bot.zaloplatforms.com"
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-lg border border-[#d6bd7c] bg-[#fdf6e9] px-2.5 py-1.5 text-[11px] font-bold text-[#865916] hover:bg-[#e9d6aa]/40"
                    >
                      🌐 Mở bot.zaloplatforms.com lấy Token
                    </a>
                  </div>
                  {activeMessaging?.zalo.lastError && (
                    <div className="text-[10px] text-[#865916]">{activeMessaging.zalo.lastError}</div>
                  )}
                </div>

                {/* Telegram Config */}
                <div className="rounded-lg border border-[#d6bd7c] bg-white p-2.5 space-y-2">
                  <div className="flex items-center justify-between text-xs font-bold text-[#2f2414]">
                    <span>2. Cấu hình Telegram Bot (@BotFather — Dự phòng / Song song)</span>
                    <span className="text-[10px] text-[#865916]">
                      {activeMessaging?.telegram.hasToken ? `Đang dùng: ${activeMessaging.telegram.maskedToken}` : 'Chưa nhập Token'}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="password"
                      value={telegramTokenInput}
                      onChange={e => setTelegramTokenInput(e.target.value)}
                      placeholder="Dán TELEGRAM_BOT_TOKEN (VD: 789012:AAF...)…"
                      className="rounded-lg border border-[#d6bd7c] px-2.5 py-1.5 text-xs text-[#2f2414]"
                    />
                    <input
                      type="text"
                      value={telegramChatIdInput}
                      onChange={e => setTelegramChatIdInput(e.target.value)}
                      placeholder="Telegram Chat ID (tự điền khi nhắn /start)"
                      className="rounded-lg border border-[#d6bd7c] px-2.5 py-1.5 text-xs text-[#2f2414]"
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 pt-0.5">
                    <button
                      type="button"
                      disabled={messagingSaving}
                      onClick={() => void handleSaveMessagingConfig('telegram')}
                      className="rounded-lg bg-[#865916] px-3 py-1.5 text-xs font-bold text-[#fff8ec] hover:bg-[#6f4810] disabled:opacity-50"
                    >
                      {messagingSaving ? 'Đang kết nối…' : '🔗 Kết Nối Telegram'}
                    </button>
                  </div>
                  {activeMessaging?.telegram.lastError && (
                    <div className="text-[10px] text-red-700">Lưu ý Telegram: {activeMessaging.telegram.lastError}</div>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    disabled={messagingSaving}
                    onClick={() => void handleSaveMessagingConfig('save')}
                    className="rounded-lg bg-[#9e2a2b] px-3 py-1.5 text-xs font-bold text-[#fff8ec] hover:bg-[#7d1f20] disabled:opacity-50"
                  >
                    {messagingSaving ? 'Đang lưu & xác thực…' : '💾 Lưu Cấu Hình Zalo / Telegram'}
                  </button>
                  <button
                    type="button"
                    disabled={messagingSending}
                    onClick={() => void handleSendTestMessagingReport()}
                    className="rounded-lg bg-[#865916] px-3 py-1.5 text-xs font-bold text-[#fff8ec] hover:bg-[#6f4810] disabled:opacity-50"
                  >
                    {messagingSending ? 'Đang gửi…' : '📲 Bắn Báo Cáo Thử Qua Zalo / Telegram'}
                  </button>
                  <button
                    type="button"
                    disabled={messagingSending}
                    onClick={() => void handlePollMessagingNow()}
                    className="rounded-lg border border-[#9e2a2b] bg-white px-3 py-1.5 text-xs font-bold text-[#9e2a2b] hover:bg-[#9e2a2b]/10 disabled:opacity-50"
                  >
                    🔄 Quét Tin Nhắn Mới & Tự Bắt Chat ID
                  </button>
                </div>
              </div>

              {/* Direct 2-Way Zalo Chat Bar */}
              <div className="rounded-lg border border-[#9e2a2b]/40 bg-white p-2.5 space-y-2">
                <div className="text-[11px] font-black text-[#2f2414]">
                  💬 Nhắn Tin Zalo 2 Chiều Trực Tiếp Với Hermes 24/7 (Giao Việc / Nhận Báo Cáo Ngay Lập Tức):
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={zaloDirectMessage}
                    onChange={e => setZaloDirectMessage(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') void handleSendInteractiveZaloChat();
                    }}
                    placeholder="Nhập tin nhắn Zalo gửi cho Hermes (VD: báo cáo sản phẩm win, lên kịch bản sốt muối chấm)…"
                    className="flex-1 rounded-lg border border-[#d6bd7c] px-2.5 py-1.5 text-xs text-[#2f2414]"
                  />
                  <button
                    type="button"
                    disabled={zaloDirectSending}
                    onClick={() => void handleSendInteractiveZaloChat()}
                    className="rounded-lg bg-[#9e2a2b] px-3 py-1.5 text-xs font-bold text-[#fff8ec] hover:bg-[#7d1f20] disabled:opacity-50"
                  >
                    {zaloDirectSending ? 'Hermes đang trả lời Zalo…' : '📨 Gửi Tin Nhắn Zalo → Hermes'}
                  </button>
                </div>
              </div>

              {activeMessaging?.recentMessages && activeMessaging.recentMessages.length > 0 && (
                <div className="rounded-lg border border-[#d6bd7c] bg-white p-2.5 space-y-1.5 max-h-48 overflow-y-auto">
                  <div className="text-[11px] font-black text-[#2f2414]">Nhật ký hội thoại Zalo / Telegram gần nhất:</div>
                  {activeMessaging.recentMessages.slice(0, 8).map(m => (
                    <div key={m.id} className="text-[11px] text-[#2f2414] border-b border-[#d6bd7c]/30 pb-1 whitespace-pre-line">
                      <span className="font-bold uppercase text-[#9e2a2b]">[{m.platform}]</span>{' '}
                      <span className="font-semibold text-[#865916]">
                        {m.direction === 'inbound' ? `👤 ${m.senderName || m.chatId}:` : `🤖 Hermes (${m.modelUsed || 'Cluster'}):`}
                      </span>{' '}
                      <span>{m.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Interactive Chat Bar with Hermes 24/7 */}
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <input
              type="text"
              value={hermesQuestion}
              onChange={e => setHermesQuestion(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') void handleAskHermes();
              }}
              placeholder="Giao việc hoặc hỏi Trợ lý Hermes 24/7 trên Cluster (VD: Tìm sản phẩm ăn vặt đang Win và gợi ý hook 3 giây đầu)…"
              className="flex-1 rounded-xl border border-[#d6bd7c] bg-white px-3 py-2 text-xs text-[#2f2414] focus:border-[#9e2a2b] focus:outline-none"
            />
            <button
              type="button"
              disabled={hermesAsking}
              onClick={() => void handleAskHermes()}
              className="flex items-center justify-center gap-1.5 rounded-xl bg-[#865916] px-4 py-2 text-xs font-bold text-[#fff8ec] hover:bg-[#6f4810] disabled:opacity-50"
            >
              <Send className="w-3.5 h-3.5" />
              {hermesAsking ? 'Hermes đang phân tích trên Cluster…' : 'Hỏi Hermes 24/7'}
            </button>
          </div>

          {hermesReply && (
            <div className="rounded-xl border border-[#9e2a2b]/40 bg-[#fdf6e9] p-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] font-bold text-[#9e2a2b]">
                <span>
                  Phản hồi từ Trợ Lý Hermes 24/7 · Model: {hermesReply.modelUsed}
                  {hermesReply.latencyMs ? ` (${hermesReply.latencyMs} ms)` : ''}
                </span>
                <span>{new Date(hermesReply.timestamp).toLocaleTimeString('vi-VN')}</span>
              </div>
              <p className="whitespace-pre-line text-xs text-[#2f2414] leading-relaxed">
                {hermesReply.replyVi}
              </p>
              {hermesReply.suggestedActions && hermesReply.suggestedActions.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {hermesReply.suggestedActions.map((act, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => {
                        if (act.action === 'generate_script') {
                          if (typeof act.payload?.productName === 'string') {
                            setScriptProductName(act.payload.productName);
                          }
                          setV3RightTab('scriptStudio');
                          void handleGenerateScript();
                        } else if (act.action === 'sync_mmo') {
                          void handleSyncMmo();
                        } else if (act.action === 'audit_channel') {
                          setV3RightTab('channelViral');
                          void handleRunChannelHealth();
                        } else if (act.action === 'patrol_now') {
                          void handleHermesPatrolNow();
                        }
                      }}
                      className="rounded-lg border border-[#9e2a2b] bg-white px-2.5 py-1 text-[11px] font-bold text-[#9e2a2b] hover:bg-[#9e2a2b] hover:text-[#fff8ec] transition-colors"
                    >
                      → {act.labelVi}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Main 2-column grid */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
          {/* ── Left: v3 Harness + Inherited v2 Configuration Form ── */}
          <div className="xl:col-span-5 rounded-2xl border border-[#d6bd7c] bg-[#fff8ec] shadow-sm p-5 space-y-5">
            <div className="flex items-center justify-between border-b border-[#d6bd7c]/50 pb-3">
              <div className="flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-[#9e2a2b]" />
                <h2 className="text-sm font-black text-[#2f2414]">Pipeline v3 & Harness Configuration</h2>
              </div>
              <span className="text-[11px] font-bold text-[#9e2a2b]">Harness Governed</span>
            </div>

            {/* ── Upgrade 1: Ông Chú MMO 20-Niche Selector & Automated Today_Win_Products <-> Win_Products Sync ── */}
            <div className="rounded-xl border border-[#865916]/35 bg-[#fdf6e9] p-3.5 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <Flame className="w-4 h-4 text-[#9e2a2b]" />
                  <span className="text-xs font-black text-[#2f2414]">
                    1. Đồng bộ Ông Chú MMO (20 Ngách) · Today_Win_Products ↔ Win_Products
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedNiches(['snacks', 'candy', 'sauce', 'dried', 'farm'])}
                    className="text-[11px] font-bold text-[#9e2a2b] hover:underline"
                  >
                    Mặc định 5 Ngách Đồ Ăn
                  </button>
                  <button
                    type="button"
                    onClick={() => useStore.getState().setActiveTab('youwee')}
                    className="text-[11px] font-bold text-[#b54426] hover:underline"
                  >
                    Mở Tab Youwee / Research →
                  </button>
                </div>
              </div>

              <div className="rounded-lg border border-[#b54426]/25 bg-[#fff8ec] px-2.5 py-2 text-[11px] text-[#5c4938] leading-relaxed">
                <div>
                  • <strong>UGC Master đọc sản phẩm Win từ:</strong>{' '}
                  <code className="font-mono font-bold text-[#9e2a2b]">/home/ai/homelab/DDlab.cloud/data/Products/Today_Win_Products</code>
                </div>
                <div className="mt-0.5">
                  • <strong>Quy trình tự động khi bấm Đồng bộ:</strong> Chuyển sản phẩm Win của những ngày trước sang{' '}
                  <code className="font-mono font-bold text-[#865916]">/data/Products/Win_Products</code> → lấy dữ liệu mới nhất từ Ông Chú MMO → so sánh với <code>Win_Products</code> (nếu trùng thì <strong>copy sang Today_Win_Products</strong>, nếu sản phẩm Win mới thì <strong>tải &amp; lọc &gt;= 6 ảnh mới vào Today_Win_Products</strong>).
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {ALL_MMO_NICHES.filter(n => showExtendedNiches || n.group === 'food').map(niche => {
                  const active = selectedNiches.includes(niche.id);
                  return (
                    <button
                      key={niche.id}
                      type="button"
                      onClick={() => toggleNiche(niche.id)}
                      className={`rounded-lg border px-2.5 py-1 text-[11px] font-bold transition-all ${
                        active
                          ? 'border-[#9e2a2b] bg-[#9e2a2b] text-[#fff8ec]'
                          : 'border-[#d6bd7c] bg-white text-[#6d5b3f] hover:bg-[#e9d6aa]/40'
                      }`}
                    >
                      {niche.label} ({niche.id})
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setShowExtendedNiches(v => !v)}
                  className="text-[11px] font-bold text-[#865916] hover:underline"
                >
                  {showExtendedNiches ? 'Thu gọn 15 ngách mở rộng' : '+ Mở rộng thêm 15 ngách khác (Gia dụng, Mỹ phẩm, Thời trang…)'}
                </button>
                <button
                  type="button"
                  disabled={mmoSyncing}
                  onClick={() => void handleSyncMmo()}
                  className="rounded-lg bg-[#9e2a2b] px-3 py-1.5 text-xs font-bold text-[#fff8ec] hover:bg-[#7d1f20] disabled:opacity-50"
                >
                  {mmoSyncing ? 'Đang đồng bộ và kiểm tra ảnh…' : 'Chọn và đồng bộ 10 Win hôm nay'}
                </button>
                {mmoSyncing && mmoJobId && (
                  <button type="button" disabled={mmoStopping} onClick={()=>void handleStopMmo()}
                    className="rounded-lg border border-[#9e2a2b] px-3 py-1.5 text-xs font-bold text-[#9e2a2b] disabled:opacity-50">
                    {mmoStopping?'Đang dừng…':'Dừng đồng bộ'}
                  </button>
                )}
              </div>
              {mmoSyncSummary && (
                <p role="status" aria-live="polite" className="text-[11px] font-bold text-[#9e2a2b] bg-[#b54426]/10 border border-[#b54426]/25 rounded p-2">{mmoSyncSummary}</p>
              )}

              {/* Automatic Today_Win_Products & Competitor Video Mapping Summary */}
              {syncedWinInventory.length > 0 && (
                <div className="rounded-lg border border-[#d6bd7c] bg-white/80 p-2.5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-black text-[#2f2414]">
                      Danh sách Today_Win_Products ({syncedWinInventory.length} SP · Đang kích hoạt cho UGC Master)
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowWinSyncList(v => !v)}
                      className="text-[11px] font-bold text-[#865916] hover:underline"
                    >
                      {showWinSyncList ? 'Thu gọn danh sách' : 'Xem chi tiết Mapping & Ảnh'}
                    </button>
                  </div>
                  {showWinSyncList && (
                    <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
                      {syncedWinInventory.map(winItem => {
                        const mappedRow = syncedMappingTable.find(
                          m => m.winProductFolder === winItem.folderName || m.productName === winItem.folderName,
                        );
                        const actionType = winSyncActions[winItem.folderName];
                        return (
                          <div
                            key={winItem.folderName}
                            className="flex items-center justify-between gap-2 rounded border border-[#e6d5a9] bg-[#fdf9f0] px-2.5 py-1.5 text-[11px]"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="font-bold text-[#2f2414] truncate">
                                {winItem.dailyRank?`#${winItem.dailyRank} · `:''}{winItem.folderName}{' '}
                                {actionType === 'copied_from_win_products' && (
                                  <span className="ml-1 rounded bg-[#b87a14]/15 px-1.5 py-0.5 text-[10px] font-extrabold text-[#865916]">
                                    Copy từ Win_Products → Today_Win_Products
                                  </span>
                                )}
                                {actionType === 'downloaded_new_win_images' && (
                                  <span className="ml-1 rounded bg-[#9e2a2b]/15 px-1.5 py-0.5 text-[10px] font-extrabold text-[#9e2a2b]">
                                    SP Win Mới (Đã tải ảnh mới)
                                  </span>
                                )}
                                {winItem.isDiyCookingProduct && (
                                  <span className="ml-1 rounded bg-[#865916]/15 px-1.5 py-0.5 text-[10px] font-extrabold text-[#865916]">
                                    Cần ảnh chế biến và thành phẩm
                                  </span>
                                )}
                              </div>
                              <div className="text-[10.5px] text-[#6d5b3f] truncate" title={winItem.selectionReasons?.join(" · ")}>
                                {winItem.selectionReasons?.join(" · ")}<br/>
                                {mappedRow && mappedRow.videoFilename !== '(Chưa có video đối thủ khớp trực tiếp)' ? (
                                  <>
                                    Video đối thủ: <code>{mappedRow.videoFilename}</code> ({mappedRow.competitorHandle}) ·{' '}
                                    <span className="font-semibold text-[#865916]">{mappedRow.duplicateNote}</span>
                                  </>
                                ) : (
                                  <> Lưu tại <code>/home/ai/homelab/DDlab.cloud/data/Products/Today_Win_Products/{winItem.folderName}</code></>
                                )}
                              </div>
                            </div>
                            <span
                              className={`shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-extrabold ${
                                winItem.curationReport?.policyVersion === 5 && winItem.curationReport.readyForProduction === true
                                  ? 'bg-[#9e2a2b]/15 text-[#9e2a2b]'
                                  : 'bg-[#865916]/15 text-[#865916]'
                              }`}
                            >
                              {winItem.imageFiles.length} ảnh đã kiểm định · {winItem.sourceImageCount??0} file nguồn
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>


            {/* ── Upgrade 2: 4-Voice Selector ── */}
            <div className="rounded-xl border border-[#d6bd7c]/80 bg-[#fdf6e9] p-3.5 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-black text-[#2f2414]">2. Giọng Kịch Bản Affiliate (4 Giọng Văn · Thực Thi Trong Pipeline)</span>
                <button
                  type="button"
                  onClick={() => setV3RightTab('scriptStudio')}
                  className="text-[11px] font-bold text-[#b54426] hover:underline"
                >
                  Mở AI Script Studio →
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {VOICE_OPTIONS.map(v => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => {
                      setSelectedTone(v.id);
                      updateUgcFlowkitConfig('scriptTone', v.id);
                    }}
                    className={`rounded-lg border p-2 text-left transition-all ${
                      selectedTone === v.id
                        ? 'border-[#865916] bg-[#865916]/15 text-[#2f2414] shadow-sm'
                        : 'border-[#d6bd7c] bg-white text-[#6d5b3f] hover:bg-[#e9d6aa]/30'
                    }`}
                  >
                    <div className="text-xs font-black">{v.label}</div>
                    <div className="text-[10px] opacity-80 mt-0.5">{v.desc}</div>
                  </button>
                ))}
              </div>
              <div className="rounded-lg border border-[#b54426]/30 bg-[#b54426]/10 px-2.5 py-1.5 text-[10.5px] font-semibold text-[#2f2414]">
                ✓ Khi bấm <b>Start Affiliate Harness Pipeline</b>, giọng văn đang chọn (<b>{VOICE_OPTIONS.find(v => v.id === selectedTone)?.label}</b>) sẽ tự động chạy qua <b>AI Script Studio</b> + <b>RAG_KBS_MCP (UGC_Food_review)</b> + <b>Visual DNA (@taphoabeoo235, @taphoacoc)</b> để nâng cấp trực tiếp Prompt ảnh/video/VO cho Google Flow (Kênh <code>{ugcFlowkitConfig.channelHandle || '@dundimmean'}</code>).
              </div>
            </div>

            {/* ── Upgrade 3: Locked 5-Axis Viral Attractiveness Gate Summary ── */}
            <div className="rounded-xl border border-[#b87a14]/40 bg-[#b87a14]/10 p-3 space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs font-black text-[#865916]">
                <ShieldCheck className="w-4 h-4" />
                Locked 5-Axis Viral Attractiveness Gate
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-[11px] font-mono text-[#2f2414]">
                <span>• scrollStopHook ≥ 8.5</span>
                <span>• visualMomentum ≥ 8.5</span>
                <span>• sensoryAppetite ≥ 9.0</span>
                <span>• conversionPsychology ≥ 8.5</span>
                <span className="sm:col-span-2">• continuityAndTruth ≥ 8.5 (Auto-Rework on fail)</span>
              </div>
            </div>

            {/* ── Inherited v2 Pipeline Form ── */}
            <form onSubmit={event => { event.preventDefault(); void handleStartUgcFlowkit('v3'); }} className="space-y-4 pt-2 border-t border-[#d6bd7c]/50">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-xs font-bold text-[#6d5b3f]">
                  Win products
                  <div className="mt-1 flex gap-2">
                    <select
                      value={ugcFlowkitConfig.winProducts === 'all' ? 'all' : 'count'}
                      onChange={event => updateUgcFlowkitConfig('winProducts', event.target.value === 'all' ? 'all' : 1)}
                      className="rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold text-[#2f2414]"
                    >
                      <option value="all">All</option>
                      <option value="count">Count</option>
                    </select>
                    {ugcFlowkitConfig.winProducts !== 'all' && (
                      <input
                        aria-label="Win product count v3"
                        required
                        type="number"
                        min="0"
                        max="10000"
                        value={ugcFlowkitConfig.winProducts}
                        onChange={event => updateUgcFlowkitConfig('winProducts', Number(event.target.value))}
                        className="w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold"
                      />
                    )}
                  </div>
                </label>

                <label className="text-xs font-bold text-[#6d5b3f]">
                  Duration (seconds)
                  <input
                    required
                    type="number"
                    min="8"
                    max="120"
                    value={ugcFlowkitConfig.durationSec}
                    onChange={event => updateUgcFlowkitConfig('durationSec', Number(event.target.value))}
                    className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold text-[#2f2414]"
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs font-bold text-[#6d5b3f]">
                  Best products
                  <input
                    required
                    type="number"
                    min="0"
                    max="10000"
                    value={ugcFlowkitConfig.bestProducts}
                    onChange={event => updateUgcFlowkitConfig('bestProducts', Number(event.target.value))}
                    className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold text-[#2f2414]"
                  />
                </label>
                <label className="text-xs font-bold text-[#6d5b3f]">
                  Good products
                  <input
                    required
                    type="number"
                    min="0"
                    max="10000"
                    value={ugcFlowkitConfig.goodProducts}
                    onChange={event => updateUgcFlowkitConfig('goodProducts', Number(event.target.value))}
                    className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold text-[#2f2414]"
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs font-bold text-[#6d5b3f]">
                  Mode
                  <select
                    value={ugcFlowkitConfig.mode}
                    onChange={event => updateUgcFlowkitConfig('mode', event.target.value as UgcFlowkitLaunchConfig['mode'])}
                    className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold text-[#2f2414]"
                  >
                    <option value="faceless">Faceless</option>
                    <option value="character">Character family</option>
                  </select>
                </label>
                <label className="text-xs font-bold text-[#6d5b3f]">
                  Location
                  <select
                    value={ugcFlowkitConfig.locationStrategy}
                    onChange={event => updateUgcFlowkitConfig('locationStrategy', event.target.value as UgcFlowkitLaunchConfig['locationStrategy'])}
                    className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold text-[#2f2414]"
                  >
                    <option value="generated-per-video">Generated per video</option>
                    <option value="folder">Use location folder</option>
                  </select>
                </label>
              </div>

              <div className="space-y-3">
                <label className="block text-xs font-bold text-[#6d5b3f]">
                  Product images folder
                  <input
                    required
                    value={ugcFlowkitConfig.productImageFolder}
                    onChange={event => updateUgcFlowkitConfig('productImageFolder', event.target.value)}
                    className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-mono text-[#2f2414]"
                  />
                </label>
                <label className="block text-xs font-bold text-[#6d5b3f]">
                  Family character folder (shared voices & portraits)
                  <input
                    required
                    value={ugcFlowkitConfig.familyCharacterFolder}
                    onChange={event => updateUgcFlowkitConfig('familyCharacterFolder', event.target.value)}
                    className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-mono text-[#2f2414]"
                  />
                </label>
              </div>

              {/* Prompt Generate Only & Automation */}
              <div className="space-y-2.5">
                <div className={`rounded-xl border p-3.5 transition-all ${
                  ugcFlowkitConfig.promptOnly
                    ? 'border-[#b54426] bg-[#b54426]/10 shadow-sm'
                    : 'border-[#d6bd7c]/60 bg-[#fdf6e9]'
                }`}>
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(ugcFlowkitConfig.promptOnly)}
                      onChange={event => {
                        const checked = event.target.checked;
                        updateUgcFlowkitConfig('promptOnly', checked);
                        if (checked) {
                          updateUgcFlowkitConfig('autoGenerate', false);
                          updateUgcFlowkitConfig('autoMerge', false);
                        }
                      }}
                      className="mt-0.5 h-4 w-4 rounded accent-[#b54426]"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 text-xs font-black text-[#7d1f20]">
                        <Sparkles className="w-3.5 h-3.5 text-[#b54426]" />
                        Prompt Generate Only (Google Flow manual copy-paste)
                      </div>
                    </div>
                  </label>
                </div>

                <div className="flex flex-wrap items-center gap-4 rounded-xl border border-[#d6bd7c]/60 bg-[#fdf6e9] p-3 text-xs text-[#2f2414]">
                  <label className="flex items-center gap-2 font-bold">
                    <input
                      type="checkbox"
                      disabled={Boolean(ugcFlowkitConfig.promptOnly)}
                      checked={ugcFlowkitConfig.autoGenerate && !ugcFlowkitConfig.promptOnly}
                      onChange={event => updateUgcFlowkitConfig('autoGenerate', event.target.checked)}
                      className="accent-[#9e2a2b]"
                    />
                    Auto generate
                  </label>
                  <label className="flex items-center gap-2 font-bold">
                    <input
                      type="checkbox"
                      disabled={!ugcFlowkitConfig.autoGenerate || Boolean(ugcFlowkitConfig.promptOnly)}
                      checked={ugcFlowkitConfig.autoMerge && !ugcFlowkitConfig.promptOnly}
                      onChange={event => updateUgcFlowkitConfig('autoMerge', event.target.checked)}
                      className="accent-[#9e2a2b]"
                    />
                    Auto merge
                  </label>
                </div>
              </div>

              {/* Preview Selection */}
              <div className="rounded-xl border border-[#865916]/30 bg-[#865916]/5 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#2f2414]">Product Selection Preview</span>
                  <button
                    type="button"
                    disabled={ugcFlowkitStarting}
                    onClick={() => void handlePreviewUgcFlowkit()}
                    className="rounded-lg border border-[#865916] bg-white px-2.5 py-1 text-xs font-bold text-[#865916] hover:bg-[#865916]/10"
                  >
                    Preview selection
                  </button>
                </div>
                {currentUgcFlowkitPreview && (
                  <div className="rounded-lg bg-white p-2.5 text-xs text-[#2f2414] border border-[#d6bd7c]/60">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-bold text-[#9e2a2b]">
                        Selected: {selectedUgcFlowkitProducts.length} / {currentUgcFlowkitPreview.counts.total} products
                      </p>
                      <div className="flex gap-2 text-[11px] font-bold">
                        <button type="button" onClick={() => setUgcFlowkitSelectedProductIds(currentUgcFlowkitPreview.products.map(p => p.id))} className="text-[#9e2a2b] hover:underline">Select all</button>
                        <button type="button" onClick={() => setUgcFlowkitSelectedProductIds([])} className="text-[#8c2d1f] hover:underline">Clear</button>
                      </div>
                    </div>
                    <div className="mt-2 max-h-40 space-y-1 overflow-y-auto">
                      {currentUgcFlowkitPreview.products.map(product => (
                        <label key={product.id} className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-[#e9d6aa]/30 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedUgcFlowkitProductIds.has(product.id)}
                            onChange={event => toggleUgcFlowkitProduct(product.id, event.target.checked)}
                            className="accent-[#9e2a2b]"
                          />
                          <span className="min-w-0 flex-1 truncate text-[11px]">{product.name}</span>
                          <span className="text-[10px] font-bold uppercase text-[#865916]">{product.id.split(':', 1)[0]}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={ugcFlowkitStarting || Boolean(currentUgcFlowkitPreview && selectedUgcFlowkitProducts.length === 0)}
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#9e2a2b] py-3 text-sm font-black text-[#fff8ec] hover:bg-[#7d1f20] shadow-md transition-all disabled:opacity-50"
              >
                <PlayCircle className="w-5 h-5" />
                {ugcFlowkitStarting ? 'Validating & Queuing Pipeline v3…' : 'Start Affiliate Harness Pipeline (v3)'}
              </button>
            </form>
          </div>

          {/* ── Right: 5-Tab Workspace (Inherited Prompts & Batches + Script Studio + Channel/Viral + Ledger) ── */}
          <div className="xl:col-span-7 space-y-4">
            <div className="flex flex-wrap items-center gap-1.5 border-b border-[#d6bd7c]/60 pb-2">
              <button
                type="button"
                onClick={() => {
                  setV3RightTab('prompts');
                  setV2RightTab?.('prompts');
                }}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                  v3RightTab === 'prompts' ? 'bg-[#865916] text-[#fff8ec]' : 'text-[#6d5b3f] hover:bg-[#e9d6aa]/50'
                }`}
              >
                <Film className="w-3.5 h-3.5" />
                Kịch bản & Prompts {v2Batch ? `(${v2Batch.products.length} SP · ${v2Batch.id.slice(0, 12)}…)` : ''}
              </button>
              <button
                type="button"
                onClick={() => {
                  setV3RightTab('batches');
                  setV2RightTab?.('batches');
                }}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                  v3RightTab === 'batches' ? 'bg-[#865916] text-[#fff8ec]' : 'text-[#6d5b3f] hover:bg-[#e9d6aa]/50'
                }`}
              >
                <History className="w-3.5 h-3.5" />
                Batches & Hàng đợi
              </button>
              <button
                type="button"
                onClick={() => setV3RightTab('scriptStudio')}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                  v3RightTab === 'scriptStudio' ? 'bg-[#9e2a2b] text-[#fff8ec]' : 'text-[#6d5b3f] hover:bg-[#e9d6aa]/50'
                }`}
              >
                <Wand2 className="w-3.5 h-3.5" />
                AI Script Studio (4 Giọng)
              </button>
              <button
                type="button"
                onClick={() => setV3RightTab('channelViral')}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                  v3RightTab === 'channelViral' ? 'bg-[#9e2a2b] text-[#fff8ec]' : 'text-[#6d5b3f] hover:bg-[#e9d6aa]/50'
                }`}
              >
                <Activity className="w-3.5 h-3.5" />
                Khám Kênh & Giải Phẫu Viral
              </button>
              <button
                type="button"
                onClick={() => setV3RightTab('ledger')}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                  v3RightTab === 'ledger' ? 'bg-[#9e2a2b] text-[#fff8ec]' : 'text-[#6d5b3f] hover:bg-[#e9d6aa]/50'
                }`}
              >
                <FileSpreadsheet className="w-3.5 h-3.5" />
                Harness Ledger
              </button>
            </div>

            {/* Tab 1: Batches Queue (Inherited from v2) */}
            {v3RightTab === 'batches' && (
              <UgcBatchesQueueView
                selectedBatchId={v2Batch?.id || ugcFlowkitBatchId}
                onSelectBatch={async (batchId) => {
                  setExplicitlySelectedBatchId(batchId);
                  setUgcFlowkitBatchId(batchId);
                  setV3RightTab('prompts');
                  setV2RightTab?.('prompts');
                  try {
                    const loaded = await getUgcFlowkitBatch(batchId);
                    setV2Batch(loaded);
                  } catch {
                    // ignore
                  }
                }}
                onActionComplete={() => {
                  void refreshV2Batch();
                }}
              />
            )}

            {/* Tab 2: AI Script Studio (4 Voices) */}
            {v3RightTab === 'scriptStudio' && (
              <div className="rounded-2xl border border-[#d6bd7c] bg-[#fff8ec] p-5 shadow-sm space-y-4">
                <div className="flex items-center justify-between border-b border-[#d6bd7c]/50 pb-3">
                  <div>
                    <h3 className="text-sm font-black text-[#2f2414]">AI Affiliate Script Studio (4 Giọng Văn Bản Địa)</h3>
                    <p className="text-xs text-[#6d5b3f]">
                      Chạy trực tiếp trên DDlab.cloud (Codex/Gemini) với tường lửa dữ liệu <code>&lt;&lt;&lt;DATA ... DATA&gt;&gt;&gt;</code> — không phụ thuộc tab ChatGPT ngoài.
                    </p>
                  </div>
                  <span className="rounded bg-[#9e2a2b]/15 px-2.5 py-1 text-xs font-bold text-[#9e2a2b]">
                    Giọng: {VOICE_OPTIONS.find(v => v.id === selectedTone)?.label}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label className="sm:col-span-2 text-xs font-bold text-[#6d5b3f]">
                    Tên sản phẩm
                    <input
                      value={scriptProductName}
                      onChange={e => setScriptProductName(e.target.value)}
                      className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold text-[#2f2414]"
                    />
                  </label>
                  <label className="text-xs font-bold text-[#6d5b3f]">
                    Giá hiển thị
                    <input
                      value={scriptPriceText}
                      onChange={e => setScriptPriceText(e.target.value)}
                      className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold text-[#2f2414]"
                    />
                  </label>
                  <label className="text-xs font-bold text-[#6d5b3f]">
                    Lượt bán
                    <input
                      type="number"
                      value={scriptSold}
                      onChange={e => setScriptSold(Number(e.target.value))}
                      className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold text-[#2f2414]"
                    />
                  </label>
                  <label className="text-xs font-bold text-[#6d5b3f]">
                    Câu hỏi người mua (TPTCommentIntent)
                    <input
                      value={scriptQuestions}
                      onChange={e => setScriptQuestions(e.target.value)}
                      className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs text-[#2f2414]"
                    />
                  </label>
                  <label className="text-xs font-bold text-[#6d5b3f]">
                    Băn khoăn / Rào cản mua
                    <input
                      value={scriptObjections}
                      onChange={e => setScriptObjections(e.target.value)}
                      className="mt-1 block w-full rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs text-[#2f2414]"
                    />
                  </label>
                </div>

                <button
                  type="button"
                  disabled={scriptLoading}
                  onClick={() => void handleGenerateScript()}
                  className="rounded-xl bg-[#9e2a2b] px-4 py-2 text-xs font-black text-[#fff8ec] hover:bg-[#7d1f20] disabled:opacity-50"
                >
                  {scriptLoading ? 'Đang phân tích & viết 3 kịch bản…' : 'Tạo Phân Tích + 3 Hooks + 3 Kịch Bản'}
                </button>

                {scriptError && (
                  <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-800">{scriptError}</div>
                )}

                {scriptResult && (
                  <div className="space-y-4 pt-2">
                    <div className="rounded-xl border border-[#865916]/30 bg-[#fdf6e9] p-3.5 space-y-1.5 text-xs">
                      <div className="font-black text-[#865916] uppercase">Phân tích Cấu trúc Chuyển đổi</div>
                      <div><strong>Hook:</strong> {scriptResult.analysis.hook}</div>
                      <div><strong>Pain / Nỗi thèm:</strong> {scriptResult.analysis.pain}</div>
                      <div><strong>Demo cận cảnh:</strong> {scriptResult.analysis.demo}</div>
                      <div><strong>Proof / Bằng chứng:</strong> {scriptResult.analysis.proof}</div>
                      <div><strong>CTA:</strong> {scriptResult.analysis.cta}</div>
                    </div>

                    <div className="rounded-xl border border-[#d6bd7c] bg-white p-3.5 space-y-2 text-xs">
                      <div className="font-black text-[#2f2414]">3 Câu Mở Đầu (Hooks &lt; 15 từ)</div>
                      {scriptResult.hooks.map((h, i) => (
                        <div key={i} className="flex items-center justify-between gap-2 rounded bg-[#fdf6e9] px-2.5 py-1.5">
                          <span><strong>#{i + 1}:</strong> {h}</span>
                          <button
                            type="button"
                            onClick={() => handlePromptCopy(h, `hook-${i}`)}
                            className="text-[11px] font-bold text-[#b54426] hover:underline shrink-0"
                          >
                            {copiedPromptId === `hook-${i}` ? 'Copied ✓' : 'Copy'}
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="space-y-3">
                      {scriptResult.scripts.map((sc, idx) => (
                        <div key={idx} className="rounded-xl border border-[#d6bd7c] bg-white p-3.5 space-y-2 text-xs">
                          <div className="font-black text-[#865916]">{sc.title}</div>
                          <div className="space-y-1">
                            {sc.steps.map((st, sIdx) => (
                              <div key={sIdx} className="rounded bg-[#fdf6e9] px-2.5 py-1.5">
                                <span className="font-mono font-bold text-[#9e2a2b]">[{st.at}] ({st.role})</span> {st.text}
                              </div>
                            ))}
                          </div>
                          <div className="font-bold text-[#2f2414]">CTA: {sc.cta}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tab 3: Khám Kênh & Giải Phẫu Viral (Channel Health Coach + Youwee Deconstructor) */}
            {v3RightTab === 'channelViral' && (
              <div className="space-y-4">
                {/* Youwee Winning Video Deconstructor */}
                <div className="rounded-2xl border border-[#d6bd7c] bg-[#fff8ec] p-5 shadow-sm space-y-3">
                  <div className="flex items-center gap-2">
                    <Video className="w-4 h-4 text-[#b54426]" />
                    <h3 className="text-sm font-black text-[#2f2414]">Giải Phẫu Video Triệu View (Youwee 3-Tier Downloader)</h3>
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={refVideoUrl}
                      onChange={e => setRefVideoUrl(e.target.value)}
                      placeholder="Dán link TikTok video top thực chiến (https://www.tiktok.com/@.../video/...)"
                      className="flex-1 rounded-lg border border-[#d6bd7c] bg-white px-3 py-1.5 text-xs text-[#2f2414]"
                    />
                    <button
                      type="button"
                      disabled={deconLoading}
                      onClick={() => void handleDeconstructVideo()}
                      className="rounded-lg bg-[#b54426] px-4 py-1.5 text-xs font-bold text-white hover:bg-[#8f3219] disabled:opacity-50"
                    >
                      {deconLoading ? 'Đang tải & giải phẫu…' : 'Tải qua Youwee & Giải Phẫu'}
                    </button>
                  </div>
                  {deconResult && (
                    <div className="rounded-xl border border-[#b54426]/30 bg-white p-3.5 text-xs space-y-1.5">
                      <div className="font-bold text-[#7d1f20]">
                        Trạng thái tải: {deconResult.download.status} (Tier: {deconResult.download.tierUsed})
                      </div>
                      <div><strong>Cơ chế mở đầu 0-1.5s:</strong> {deconResult.blueprint.openingMechanic}</div>
                      <div><strong>Nhịp cắt cảnh trung bình:</strong> {deconResult.blueprint.visualPaceSec}s</div>
                      <div><strong>Mẫu câu Hook nói:</strong> {deconResult.blueprint.spokenHookPattern}</div>
                      <div><strong>Xử lý rào cản mua hàng:</strong> {deconResult.blueprint.objectionHandled}</div>
                    </div>
                  )}
                </div>

                {/* Channel Health Coach */}
                <div className="rounded-2xl border border-[#d6bd7c] bg-[#fff8ec] p-5 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-black text-[#2f2414]">AI Khám Kênh & Huấn Luyện Tăng Trưởng (Bản Nâng Cấp Thuần Việt)</h3>
                      <p className="text-xs text-[#6d5b3f]">
                        Phân tích điểm ngọt thời lượng, đối chiếu món nổ view vs. kẹt view và tự động đề xuất cấu hình tối ưu cho Pipeline v3.
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={healthLoading}
                      onClick={() => void handleRunChannelHealth()}
                      className="rounded-xl bg-[#9e2a2b] px-4 py-2 text-xs font-black text-[#fff8ec] hover:bg-[#7d1f20] disabled:opacity-50"
                    >
                      {healthLoading ? 'Đang khám kênh…' : 'Phân Tích Sức Khỏe Kênh'}
                    </button>
                  </div>

                  <textarea
                    rows={6}
                    value={channelJsonText}
                    onChange={e => setChannelJsonText(e.target.value)}
                    className="w-full rounded-xl border border-[#d6bd7c] bg-white p-3 text-xs font-mono text-[#2f2414]"
                  />

                  {healthError && (
                    <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-800">{healthError}</div>
                  )}

                  {healthReport && (
                    <div className="space-y-3 pt-2">
                      <div className="rounded-xl border border-[#9e2a2b]/40 bg-[#b87a14]/10 p-3.5 text-xs space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-black text-[#9e2a2b]">
                            Điểm ngọt thời lượng: {healthReport.sweetSpot.bestBucketLabel} (TB {healthReport.sweetSpot.bestBucketAvgViews.toLocaleString('vi-VN')} lượt xem · {healthReport.sweetSpot.bestBucketAvgSaves} lượt lưu)
                          </span>
                          <button
                            type="button"
                            onClick={() => handleApplyHarnessActions(healthReport)}
                            className="rounded-lg bg-[#9e2a2b] px-3 py-1 text-[11px] font-bold text-white hover:bg-[#7d1f20]"
                          >
                            Áp dụng cấu hình tối ưu ({healthReport.harnessActions.recommendedDurationSec}s · {healthReport.harnessActions.recommendedTone})
                          </button>
                        </div>
                        <div className="font-bold text-[#2f2414]">{healthReport.verdict}</div>
                        <p className="text-[#6d5b3f]">{healthReport.summary}</p>
                      </div>

                      {healthReport.harnessActions.rescueProducts.length > 0 && (
                        <div className="rounded-xl border border-[#865916]/30 bg-white p-3.5 text-xs space-y-1.5">
                          <div className="font-black text-[#865916]">Danh sách Món Cần Quay Lại (Rescue Products)</div>
                          {healthReport.harnessActions.rescueProducts.map((rp, i) => (
                            <div key={i} className="rounded bg-[#fdf6e9] p-2">
                              <strong>{rp.caption}:</strong> {rp.reason}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tab 4: Harness Ledger */}
            {v3RightTab === 'ledger' && (
              <div className="rounded-2xl border border-[#d6bd7c] bg-[#fff8ec] p-5 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-black text-[#2f2414]">Durable Harness Ledger (THREAD.md · results.tsv · rejected.md)</h3>
                  <button
                    type="button"
                    disabled={ledgerLoading}
                    onClick={() => void loadLedger()}
                    className="rounded-lg border border-[#d6bd7c] bg-white px-3 py-1 text-xs font-bold text-[#6d5b3f]"
                  >
                    Làm mới sổ cái
                  </button>
                </div>
                {ledgerSnapshot && (
                  <div className="space-y-3 text-xs">
                    <div>
                      <div className="font-bold text-[#865916] mb-1">results.tsv</div>
                      <pre className="rounded-lg border border-[#d6bd7c]/60 bg-white p-3 font-mono text-[11px] overflow-x-auto max-h-44">{ledgerSnapshot.resultsTsv}</pre>
                    </div>
                    <div>
                      <div className="font-bold text-[#8c2d1f] mb-1">rejected.md (Anti-Repeat Ledger)</div>
                      <pre className="rounded-lg border border-[#d6bd7c]/60 bg-white p-3 font-mono text-[11px] overflow-x-auto max-h-44">{ledgerSnapshot.rejectedMd}</pre>
                    </div>
                    <div>
                      <div className="font-bold text-[#2f2414] mb-1">THREAD.md</div>
                      <pre className="rounded-lg border border-[#d6bd7c]/60 bg-white p-3 font-mono text-[11px] overflow-x-auto max-h-44">{ledgerSnapshot.threadMd}</pre>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tab 0: Prompts & Kịch bản (Full Scenes, Reference Prompts & Merged Video) */}
            {v3RightTab === 'prompts' && (
              v2Batch ? (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-[#d6bd7c] bg-[#fff8ec] p-4 shadow-sm flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Film className="w-4 h-4 text-[#865916]" />
                        <span className="text-xs font-mono font-bold text-[#865916]">Batch {v2Batch.id}</span>
                        <span className="rounded-full bg-[#b87a14]/20 px-2 py-0.5 text-[10px] font-bold text-[#9e2a2b]">
                          {v2Batch.products.length} Products
                        </span>
                        <span className="rounded-full bg-[#476b24]/15 px-2 py-0.5 text-[10px] font-bold text-[#476b24]">
                          {v2Batch.products.filter(p => p.status === 'merged').length} Merged
                        </span>
                        <span className="rounded-full bg-[#865916]/10 px-2 py-0.5 text-[10px] font-bold text-[#865916]">
                          {v2Batch.config.mode}
                        </span>
                      </div>
                      <p className="mt-1 text-[11px] text-[#6d5b3f]">
                        Started: {new Date(v2Batch.createdAt).toLocaleString()} · Duration: {v2Batch.config.durationSec}s
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => void refreshV2Batch()}
                        disabled={v2BatchLoading}
                        className="flex items-center gap-1 rounded-lg border border-[#d6bd7c] bg-white px-2.5 py-1.5 text-xs font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/50"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${v2BatchLoading ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                      <button
                        type="button"
                        onClick={onOpenWorkspace}
                        className="flex items-center gap-1 rounded-lg bg-[#865916] px-2.5 py-1.5 text-xs font-bold text-[#fff8ec] hover:bg-[#6f4810]"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Mở UGC Flowkit
                      </button>
                      {v2Batch.runtime?.blocked && (
                        <>
                          <button
                            type="button"
                            disabled={Boolean(v2ActionLoading)}
                            onClick={() => void handleResumeV2Batch()}
                            className="rounded-lg bg-[#b87a14] px-3 py-1.5 text-xs font-bold text-white"
                          >
                            Tiếp tục chạy (Resume)
                          </button>
                          <button
                            type="button"
                            disabled={Boolean(v2ActionLoading)}
                            onClick={() => void handleStopV2Batch()}
                            className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white"
                          >
                            Dừng hẳn batch
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {v2Batch.products.map(product => {
                    const plan = product.runtime?.plan;
                    const locationPrompt = product.runtime?.productVision?.locationPrompt || plan?.location?.name || '';
                    const isStoppedSafe = product.status === 'stopped_safe';
                    const isPlanning = (product.status === 'planning' || product.runtime?.phase === 'planning') && !isStoppedSafe && !v2Batch.runtime?.blocked;
                    const isQueued = product.status === 'queued';

                    return (
                      <div key={product.id} className="rounded-2xl border border-[#d6bd7c] bg-[#fff8ec] shadow-sm overflow-hidden">
                        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#d6bd7c]/50 bg-[#f4e8c9]/50 px-4 py-3">
                          <div>
                            <h3 className="text-sm font-black text-[#2f2414]">{product.name}</h3>
                            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-[#6d5b3f]">
                              <span>Status: <strong className="capitalize">{product.status}</strong></span>
                              {product.runtime?.phase && <span>· Phase: {product.runtime.phase}</span>}
                              {product.runtime?.planner && <span>· Planner: {product.runtime.planner}</span>}
                              {product.runtime?.scriptStudio?.tone && <span>· Giọng: {product.runtime.scriptStudio.tone}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isStoppedSafe && (
                              <button
                                type="button"
                                disabled={Boolean(v2ActionLoading)}
                                onClick={() => void handleResumeProduct(product.id)}
                                className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700"
                              >
                                {v2ActionLoading === `resume-${product.id}` ? 'Đang thử lại...' : 'Thử lại (Retry)'}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handlePromptCopy(formatGoogleFlowPrompts(product), `v3-all-${product.id}`)}
                              className="flex items-center gap-1.5 rounded-lg bg-[#9e2a2b] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#7d1f20]"
                            >
                              {copiedPromptId === `v3-all-${product.id}` ? <><Check className="w-3.5 h-3.5" />Copied All!</> : <><Copy className="w-3.5 h-3.5" />Copy All Prompts</>}
                            </button>
                          </div>
                        </div>

                        <div className="p-4 space-y-3 text-xs">
                          {/* Safe Stop Banner */}
                          {isStoppedSafe && (
                            <div className="rounded-xl border border-amber-300 bg-amber-50/80 p-3 text-amber-900 space-y-1">
                              <div className="font-bold text-amber-800 flex items-center gap-1.5">
                                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                                Pipeline dừng an toàn (Safe Stop): {product.safeStop?.code || 'STOPPED'}
                              </div>
                              {product.safeStop?.message && (
                                <p className="text-amber-700 break-words font-mono text-[11px]">{product.safeStop.message}</p>
                              )}
                            </div>
                          )}

                          {/* Queue / Planning status */}
                          {isQueued && (
                            <div className="flex items-center gap-2 rounded-xl border border-amber-300/80 bg-amber-50 p-3 text-amber-800">
                              <Clock className="w-4 h-4 text-amber-600 shrink-0" />
                              <span>Đang trong hàng đợi — Sẽ bắt đầu phân tích hình ảnh và tạo kịch bản khi tới lượt.</span>
                            </div>
                          )}
                          {isPlanning && !isQueued && (
                            <div className="flex items-center gap-2 rounded-xl border border-[#b87a14]/40 bg-[#b87a14]/10 p-3 text-[#865916]">
                              <RefreshCw className="w-4 h-4 animate-spin text-[#865916] shrink-0" />
                              <span>AI Planner đang phân tích hình ảnh sản phẩm và tạo bộ prompts…</span>
                            </div>
                          )}

                          {/* Merged Video Player */}
                          {product.runtime?.merged?.url && (
                            <div className="rounded-xl border border-[#76a441]/50 bg-[#76a441]/10 p-3 flex flex-wrap items-center justify-between gap-3">
                              <div className="flex items-center gap-3">
                                <video
                                  controls
                                  preload="metadata"
                                  src={product.runtime.merged.url}
                                  className="w-28 rounded-lg border border-[#d6bd7c] bg-black shadow-sm"
                                />
                                <div className="space-y-1">
                                  <div className="font-black text-[#476b24] flex items-center gap-1.5">
                                    <CheckCircle2 className="w-4 h-4" />
                                    Video hoàn chỉnh ({product.runtime.merged.clipCount} clips)
                                  </div>
                                  <div className="text-[11px] text-[#6d5b3f]">
                                    Hoàn tất: {new Date(product.runtime.merged.completedAt).toLocaleString()}
                                  </div>
                                  <a
                                    href={product.runtime.merged.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 rounded-lg bg-[#476b24] px-2.5 py-1 text-[11px] font-bold text-[#fff8ec] hover:bg-[#395719] transition-colors"
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    Mở / Tải Video
                                  </a>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Hook & Concept */}
                          {plan && (
                            <div className="rounded-xl border border-[#d6bd7c]/60 bg-[#fdf6e9] p-3 space-y-1">
                              <div><strong className="text-[#865916]">Hook:</strong> {plan.hook}</div>
                              <div><strong className="text-[#865916]">Concept:</strong> {plan.concept}</div>
                              {plan.voiceRole && (
                                <div className="text-[11px] text-[#6d5b3f]">Voice: <strong className="capitalize text-[#2f2414]">{plan.voiceRole}</strong></div>
                              )}
                            </div>
                          )}

                          {/* 1. Reference (Location) Prompt */}
                          {locationPrompt && (
                            <div className="rounded-xl border border-[#865916]/30 bg-white p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-black text-[#865916] uppercase tracking-wide">
                                  1. Reference (Location) Prompt
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handlePromptCopy(locationPrompt, `v3-loc-${product.id}`)}
                                  className="flex items-center gap-1 rounded border border-[#d6bd7c] bg-[#fff8ec] px-2 py-0.5 text-[11px] font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/50"
                                >
                                  {copiedPromptId === `v3-loc-${product.id}` ? (
                                    <span className="text-[#476b24] flex items-center gap-1"><Check className="w-3 h-3" />Copied!</span>
                                  ) : (
                                    <span className="flex items-center gap-1"><Copy className="w-3 h-3" />Copy Reference Prompt</span>
                                  )}
                                </button>
                              </div>
                              <div className="rounded border border-[#d6bd7c]/40 bg-[#fdf6e9] p-2.5 font-mono text-[#2f2414] whitespace-pre-wrap select-all">
                                {locationPrompt}
                              </div>
                            </div>
                          )}

                          {/* 2. Scenes (Frame-0 Still, Motion & VO) */}
                          {plan?.scenes && plan.scenes.length > 0 && (
                            <div className="space-y-2.5">
                              <div className="text-xs font-black text-[#865916] uppercase tracking-wide">
                                2. Scenes ({plan.scenes.length} Scenes · Frame-0 Still, Motion & VO)
                              </div>
                              {plan.scenes.map(scene => (
                                <div key={scene.number} className="rounded-xl border border-[#d6bd7c]/60 bg-white p-3 space-y-2">
                                  <div className="flex items-center justify-between border-b border-[#d6bd7c]/40 pb-1.5">
                                    <span className="text-xs font-black text-[#2f2414]">
                                      Scene {scene.number} ({scene.retainDurationSec}s) — Source: {scene.sourceReferenceName}
                                    </span>
                                  </div>

                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between text-[11px]">
                                      <span className="font-bold text-[#6d5b3f]">Frame-0 Still Prompt:</span>
                                      <button
                                        type="button"
                                        onClick={() => handlePromptCopy(scene.prompt, `v3-scene-${product.id}-${scene.number}`)}
                                        className="text-[10px] font-bold text-[#9e2a2b] hover:underline flex items-center gap-1"
                                      >
                                        {copiedPromptId === `v3-scene-${product.id}-${scene.number}` ? (
                                          <span className="text-[#476b24]">Copied ✓</span>
                                        ) : (
                                          <span>Copy Scene Prompt</span>
                                        )}
                                      </button>
                                    </div>
                                    <div className="rounded border border-[#d6bd7c]/40 bg-[#fdf6e9] p-2 font-mono text-[#2f2414] whitespace-pre-wrap select-all">
                                      {scene.prompt}
                                    </div>
                                  </div>

                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between text-[11px]">
                                      <span className="font-bold text-[#6d5b3f]">8-Second Video Motion Prompt:</span>
                                      <button
                                        type="button"
                                        onClick={() => handlePromptCopy(scene.videoPrompt, `v3-vid-${product.id}-${scene.number}`)}
                                        className="text-[10px] font-bold text-[#9e2a2b] hover:underline flex items-center gap-1"
                                      >
                                        {copiedPromptId === `v3-vid-${product.id}-${scene.number}` ? (
                                          <span className="text-[#476b24]">Copied ✓</span>
                                        ) : (
                                          <span>Copy Video Prompt</span>
                                        )}
                                      </button>
                                    </div>
                                    <div className="rounded border border-[#d6bd7c]/40 bg-[#fdf6e9] p-2 font-mono text-[#2f2414] whitespace-pre-wrap select-all">
                                      {scene.videoPrompt}
                                    </div>
                                  </div>

                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between text-[11px]">
                                      <span className="font-bold text-[#6d5b3f]">Voiceover Script:</span>
                                      <button
                                        type="button"
                                        onClick={() => handlePromptCopy(`${scene.voiceover}${scene.babyInterjection ? ` (${scene.babyInterjection})` : ''}`, `v3-vo-${product.id}-${scene.number}`)}
                                        className="text-[10px] font-bold text-[#9e2a2b] hover:underline flex items-center gap-1"
                                      >
                                        {copiedPromptId === `v3-vo-${product.id}-${scene.number}` ? (
                                          <span className="text-[#476b24]">Copied ✓</span>
                                        ) : (
                                          <span>Copy VO</span>
                                        )}
                                      </button>
                                    </div>
                                    <div className="rounded border border-[#d6bd7c]/40 bg-[#fdf6e9] p-2 text-[#2f2414] select-all">
                                      {scene.voiceover}
                                      {scene.babyInterjection && (
                                        <span className="ml-1 text-[#865916] italic">({scene.babyInterjection})</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-[#d6bd7c] bg-[#fff8ec]/60 p-8 text-center text-xs text-[#6d5b3f]">
                  Chưa có batch nào đang chọn. Hãy bấm <strong>Đồng bộ 10 Win hôm nay</strong> hoặc <strong>Preview selection → Start Affiliate Harness Pipeline (v3)</strong> ở cột trái.
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
